import React, { useState } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Chip,
  Button,
  Divider,
  Autocomplete
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import './AdvancedFilters.css';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const AdvancedFilters = ({
  customers,
  onFilterChange,
  healthStatusFilter,
  serviceDaysFilter,
  cityFilter,
  onHealthStatusChange,
  onServiceDaysChange,
  onCityChange
}) => {
  const [expanded, setExpanded] = useState(false);

  // Extract unique cities from customers
  const uniqueCities = [...new Set(customers.map(c => c.custObject?.custCity).filter(Boolean))].sort();

  // Count customers by health status
  const healthStatusCounts = {
    healthy: customers.filter(c => c.healthStatus === 'healthy').length,
    warning: customers.filter(c => c.healthStatus === 'warning').length,
    overdue: customers.filter(c => c.healthStatus === 'overdue').length,
    none: customers.filter(c => c.healthStatus === 'none').length
  };

  // Count customers by service day
  const serviceDayCounts = DAYS_OF_WEEK.reduce((acc, day) => {
    acc[day] = customers.filter(c => c.serviceDay === day).length;
    return acc;
  }, {});

  const handleHealthStatusToggle = (status) => {
    const newFilter = healthStatusFilter.includes(status)
      ? healthStatusFilter.filter(s => s !== status)
      : [...healthStatusFilter, status];
    onHealthStatusChange(newFilter);
  };

  const handleServiceDayToggle = (day) => {
    const newFilter = serviceDaysFilter.includes(day)
      ? serviceDaysFilter.filter(d => d !== day)
      : [...serviceDaysFilter, day];
    onServiceDaysChange(newFilter);
  };

  const handleClearAll = () => {
    onHealthStatusChange([]);
    onServiceDaysChange([]);
    onCityChange([]);
  };

  const hasActiveFilters = healthStatusFilter.length > 0 ||
                          serviceDaysFilter.length > 0 ||
                          cityFilter.length > 0;

  return (
    <Box sx={{ mb: 2 }}>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{
          borderRadius: 2,
          '&:before': { display: 'none' },
          boxShadow: expanded ? 3 : 1,
          transition: 'all 0.3s ease'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />}
          sx={{
            backgroundColor: '#f5f5f5',
            borderRadius: expanded ? '8px 8px 0 0' : '8px',
            padding: { xs: '8px 16px', sm: '16px' },
            minHeight: { xs: '48px', sm: '64px' },
            '&:hover': {
              backgroundColor: '#e8e8e8'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, width: '100%', flexWrap: 'wrap' }}>
            <FilterListIcon sx={{ color: '#003366', fontSize: { xs: 20, sm: 24 } }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#003366', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Advanced Filters
            </Typography>
            {hasActiveFilters && (
              <Chip
                label={`${healthStatusFilter.length + serviceDaysFilter.length + cityFilter.length} filter(s) applied`}
                size="small"
                color="primary"
                sx={{ ml: 'auto', mr: { xs: 0, sm: 2 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
              />
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
            {/* Health Status Filter */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: '#003366', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                Service Health Status
              </Typography>
              <FormGroup row sx={{ gap: { xs: 0, sm: 1 } }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={healthStatusFilter.includes('healthy')}
                      onChange={() => handleHealthStatusToggle('healthy')}
                      size={window.innerWidth < 600 ? 'small' : 'medium'}
                      sx={{ color: '#10B981', '&.Mui-checked': { color: '#10B981' } }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                      <span style={{ fontSize: window.innerWidth < 600 ? '0.85rem' : '1rem' }}>Recent</span>
                      <Chip label={healthStatusCounts.healthy} size="small" sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }} />
                    </Box>
                  }
                  sx={{ mr: { xs: 0.5, sm: 2 } }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={healthStatusFilter.includes('warning')}
                      onChange={() => handleHealthStatusToggle('warning')}
                      size={window.innerWidth < 600 ? 'small' : 'medium'}
                      sx={{ color: '#F59E0B', '&.Mui-checked': { color: '#F59E0B' } }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                      <span style={{ fontSize: window.innerWidth < 600 ? '0.85rem' : '1rem' }}>Due Soon</span>
                      <Chip label={healthStatusCounts.warning} size="small" sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }} />
                    </Box>
                  }
                  sx={{ mr: { xs: 0.5, sm: 2 } }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={healthStatusFilter.includes('overdue')}
                      onChange={() => handleHealthStatusToggle('overdue')}
                      size={window.innerWidth < 600 ? 'small' : 'medium'}
                      sx={{ color: '#EF4444', '&.Mui-checked': { color: '#EF4444' } }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                      <span style={{ fontSize: window.innerWidth < 600 ? '0.85rem' : '1rem' }}>Overdue</span>
                      <Chip label={healthStatusCounts.overdue} size="small" sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }} />
                    </Box>
                  }
                  sx={{ mr: { xs: 0.5, sm: 2 } }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={healthStatusFilter.includes('none')}
                      onChange={() => handleHealthStatusToggle('none')}
                      size={window.innerWidth < 600 ? 'small' : 'medium'}
                      sx={{ color: '#9CA3AF', '&.Mui-checked': { color: '#9CA3AF' } }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                      <span style={{ fontSize: window.innerWidth < 600 ? '0.85rem' : '1rem' }}>No History</span>
                      <Chip label={healthStatusCounts.none} size="small" sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }} />
                    </Box>
                  }
                  sx={{ mr: { xs: 0.5, sm: 2 } }}
                />
              </FormGroup>
            </Box>

            <Divider />

            {/* Service Days Filter */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: '#003366', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                Service Days
              </Typography>
              <FormGroup row sx={{ gap: { xs: 0, sm: 1 } }}>
                {DAYS_OF_WEEK.map(day => (
                  <FormControlLabel
                    key={day}
                    control={
                      <Checkbox
                        checked={serviceDaysFilter.includes(day)}
                        onChange={() => handleServiceDayToggle(day)}
                        size={window.innerWidth < 600 ? 'small' : 'medium'}
                        sx={{ color: '#003366', '&.Mui-checked': { color: '#003366' } }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                        <span style={{ fontSize: window.innerWidth < 600 ? '0.85rem' : '1rem' }}>{day}</span>
                        <Chip
                          label={serviceDayCounts[day] || 0}
                          size="small"
                          sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                        />
                      </Box>
                    }
                    sx={{ mr: { xs: 0.5, sm: 2 } }}
                  />
                ))}
              </FormGroup>
            </Box>

            <Divider />

            {/* City Filter */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: '#003366', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                Cities
              </Typography>
              <Autocomplete
                multiple
                options={uniqueCities}
                value={cityFilter}
                onChange={(event, newValue) => onCityChange(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select cities..."
                    size="small"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      size="small"
                      color="primary"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                    />
                  ))
                }
                sx={{ minWidth: { xs: '100%', sm: 300 } }}
              />
            </Box>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<ClearIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                  onClick={handleClearAll}
                  size="small"
                  fullWidth={window.innerWidth < 600}
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  Clear All Filters
                </Button>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default AdvancedFilters;
